import { CircularProgress } from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { lazy, Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Route, Routes, useNavigate } from "react-router-dom";
import ErrorComponent from "../Mobile/components/ErrorComponent";
import { useBets } from "../common/contexts/betContext";
import { useFixtures } from "../common/contexts/fixturesContext";
import { useUser } from "../common/contexts/userContext";
import { ProtectedRoute } from "../common/routing/ProtectedRoute";
import { PublicOnlyRoute } from "../common/routing/PublicOnlyRoute";
import { getToken, isSigned } from "../services/authStorage";
import { getUserBets } from "../services/betService";
import { getAllMatches } from "../services/matchService";
import { getUserById } from "../services/userService";
import { DecodedToken } from "../types/DecodedToken";
import { BrowserNavbar } from "./BrowserNavbar/BrowserNavbar";
import "./BrowserStyles.scss";

// Route-level code splitting — each page is loaded on demand.
const BrowserProfile = lazy(() => import("./BrowserProfile/BrowserProfile"));
const BrowserHomepage = lazy(() => import("./BrowserHomepage/BrowserHomepage").then(m => ({ default: m.BrowserHomepage })));
const BrowserLogin = lazy(() => import("./BrowserLogin/BrowserLogin"));
const BrowserRanking = lazy(() => import("./BrowserRanking/BrowserRanking"));
const BrowserLeagues = lazy(() => import("./BrowserLeagues/BrowserLeagues"));
const BrowserFAQ = lazy(() => import("./BrowserFAQ"));
const BrowserFixture = lazy(() => import("./BrowserFixture/BrowserFixture"));
const BrowserRules = lazy(() => import("./BrowserRules/BrowserRules"));
const BrowserBets = lazy(() => import("./BrowserBets/BrowserBets"));
const BrowserCreateAccount = lazy(() => import("./BrowserWizard/BrowserCreateAccount"));
const BrowserCreateLeague = lazy(() => import("./BrowserWizard/BrowserCreateLeague"));
const ExcelData = lazy(() => import("./ExcelData"));
const BrowserWorldCupInfo = lazy(() => import("./BrowserWorldCupInfo/BrowserWorldCupInfo"));
const MatchBetBreakdownPage = lazy(() => import("../common/pages/MatchBetBreakdownPage"));

export const BrowserApp = () => {
  const navigate = useNavigate();
  const { user, setUser, setIsLoading: setUserLoading } = useUser();
  const { setMatches, setIsLoading: setMatchesLoading } = useFixtures();
  const { setBets, setIsLoading: setBetsLoading } = useBets();

  async function updateContexts(token: DecodedToken) {
    setUserLoading(true);
    setMatchesLoading(true);
    setBetsLoading(true);
    try {
      const [matches, bets, userData] = await Promise.all([
        getAllMatches(),
        getUserBets(token.Id),
        getUserById(token.Id),
      ]);
      setMatches(matches.filter((match) => match.homeTeam && match.awayTeam));
      setBets(bets);
      setUser(userData);
    } finally {
      setUserLoading(false);
      setMatchesLoading(false);
      setBetsLoading(false);
    }
  }
  const isUserSigned = isSigned();
  useEffect(() => {
    if (isUserSigned && !user.id) {
      const tokenDecoded = jwtDecode<DecodedToken>(getToken() ?? "");
      updateContexts(tokenDecoded);
    }
    // Run-once bootstrap: hydrate user/matches/bets for an existing session.
    // updateContexts is recreated each render, so listing it (or the guard
    // values) would re-run this effect and risk a refetch loop — mount-only is
    // intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Show the top nav everywhere except the bare landing ("/") when signed out;
  // the login panel keeps it so users still see the brand/tournament header.
  const isNavbarVisibile =
    window.location.pathname !== "/" || isUserSigned;
  return (
    <div className="main-body">
      {isNavbarVisibile && <BrowserNavbar />}
      <div className="browserApp__content">
        <ErrorBoundary
          FallbackComponent={ErrorComponent}
          onReset={() => navigate("/")}
        >
        <Suspense fallback={<CircularProgress sx={{ display: 'block', margin: '4rem auto' }} />}>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <BrowserProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/homepage"
            element={
              <ProtectedRoute>
                <BrowserHomepage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bets"
            element={
              <ProtectedRoute>
                <BrowserBets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fixtures"
            element={
              <ProtectedRoute>
                <BrowserFixture />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ranking"
            element={
              <ProtectedRoute>
                <BrowserRanking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <BrowserProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leagues"
            element={
              <ProtectedRoute>
                <BrowserLeagues />
              </ProtectedRoute>
            }
          />
          <Route
            path="/faq"
            element={
              <ProtectedRoute>
                <BrowserFAQ />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rules"
            element={
              <ProtectedRoute>
                <BrowserRules />
              </ProtectedRoute>
            }
          />
          <Route
            path="/worldcup-info"
            element={
              <ProtectedRoute>
                <BrowserWorldCupInfo />
              </ProtectedRoute>
            }
          />
          <Route
            path="/match/:matchId/bets-breakdown"
            element={
              <ProtectedRoute>
                <MatchBetBreakdownPage isDesktop />
              </ProtectedRoute>
            }
          />
          <Route path='/data' element={<ExcelData />}/>
          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <BrowserLogin />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/createAccount"
            element={
              <PublicOnlyRoute>
                <BrowserCreateAccount />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/createLeague"
            element={
              <ProtectedRoute>
                <BrowserCreateLeague />
              </ProtectedRoute>
            }
          />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
};
